import type { HTMLAttributes } from 'react'
import React, { useMemo } from 'react'
import { csx, IconCaretDown, Text } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { last } from 'ramda'
import { FormattedMessage, useIntl } from 'react-intl'
import { Price } from '@/core/components'

import { useDrawer } from '../../../../ui/components'
import type { BestPaymentMethod_PaymentOptionConnection$key } from './__generated__/BestPaymentMethod_PaymentOptionConnection.graphql'
import { BestPaymentDrawer } from './BestPaymentDrawer'
import { getBestInstallmentData } from './utils'

const paymentOptionConnectionFragment = graphql`
  fragment BestPaymentMethod_PaymentOptionConnection on PaymentOptionConnection {
    edges {
      node {
        installments {
          edges {
            node {
              value {
                amount
              }
            }
          }
        }
        ...BestPaymentDrawer_PaymentOption
        ...utils_PaymentOption
      }
    }
  }
`

export type BestPaymentMethodProps = {
  paymentOptionConnectionRef: BestPaymentMethod_PaymentOptionConnection$key
} & HTMLAttributes<HTMLDivElement>

export const BestPaymentMethod = ({
  paymentOptionConnectionRef,
}: BestPaymentMethodProps) => {
  const { formatMessage } = useIntl()
  const bestPaymentDrawer = useDrawer({ route: '#/best-payment-method-drawer' })

  const handlePaymentMethodTextClick = () => {
    bestPaymentDrawer.open()
  }

  const handlePaymentDrawerClose = () => {
    bestPaymentDrawer.close()
  }

  const paymentOptionConnection = useSuspenseFragment(
    paymentOptionConnectionFragment,
    paymentOptionConnectionRef
  )

  const bestPaymentMethod = useMemo(() => {
    return paymentOptionConnection.edges.reduce((acc, currentPaymentOption) => {
      const currentPaymentOptionHasMoreInstallments =
        currentPaymentOption.node.installments.edges.length >
        acc.node.installments.edges.length

      return currentPaymentOptionHasMoreInstallments
        ? currentPaymentOption
        : acc
    }, paymentOptionConnection.edges[0])
  }, [paymentOptionConnection.edges])

  const bestInstallmentsData = getBestInstallmentData(bestPaymentMethod.node)

  const renderBestPaymentMethodText = () => {
    const installmentsCount = bestInstallmentsData.installments.length
    const lastInstallment = last(bestInstallmentsData.installments)

    if (!lastInstallment) return null

    return (
      <>
        <FormattedMessage
          id="pdp.payment.best-payment"
          values={{
            installmentsCount,
            value: <Price value={lastInstallment.node.value.amount / 100} />,
          }}
        />
        {!bestInstallmentsData.hasInterestRate && (
          <Text
            variant="action2"
            tone="positive"
            className={csx({ marginLeft: '$space-1' })}
          >
            {formatMessage({ id: 'interestFree' }).toLocaleLowerCase()}
          </Text>
        )}
      </>
    )
  }

  const hasLessThanTwoInstallments =
    bestPaymentMethod.node.installments.edges.length < 2

  if (hasLessThanTwoInstallments) {
    return null
  }

  return (
    <div
      data-testid="best-payment-method"
      className={csx({
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        marginBottom: '$space-3',
      })}
    >
      <Text
        variant="action2"
        tone="secondary"
        onClick={handlePaymentMethodTextClick}
      >
        {renderBestPaymentMethodText()}
      </Text>
      <IconCaretDown
        className={csx({ marginLeft: '$space-05', transform: 'scale(0.6)' })}
        onClick={handlePaymentMethodTextClick}
      />
      <BestPaymentDrawer
        isOpen={bestPaymentDrawer.isOpen}
        onClose={handlePaymentDrawerClose}
        paymentOptionRef={bestPaymentMethod.node}
      />
    </div>
  )
}
